import PriceFormat from '@website/components/utilities/PriceFormat';
import {
  BookingSpaceInvoiceSummaryType,
  UserBookingDetailsQuery,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import InvoiceRowItem from './InvoiceRowItem';

export default function TotalBookingValueSection({
  booking,
}: {
  booking: Pick<
    UserBookingDetailsQuery['booking'],
    'invoiceSummary' | 'spaces' | 'created'
  >;
}) {
  const totalBookingValue = useMemo(() => {
    return booking.invoiceSummary.find(
      (summary) =>
        summary.type === BookingSpaceInvoiceSummaryType.TOTAL_BOOKING_VALUE,
    );
  }, [booking.invoiceSummary]);

  const discretionaryChange = useMemo(() => {
    return booking.invoiceSummary.find(
      (summary) =>
        summary.type === BookingSpaceInvoiceSummaryType.DISCRETIONARY_CHANGE,
    );
  }, [booking.invoiceSummary]);

  const totalCancelled = useMemo(() => {
    return booking.invoiceSummary.find(
      (summary) => summary.type === BookingSpaceInvoiceSummaryType.CANCELLATION,
    );
  }, [booking.invoiceSummary]);

  return (
    <>
      <InvoiceRowItem
        lineText="Total Booking Value"
        amountZD={totalBookingValue.amount.amountZD}
        currency={totalBookingValue.amount.currency}
        amountClassName="fw-bold"
        className="fw-bold"
        trClassName="border-bottom "
      />
      <InvoiceRowItem
        lineText={
          <>
            {booking.spaces.length > 1
              ? `${booking.spaces.length} spaces`
              : '1 space'}{' '}
            booked @{' '}
            <PriceFormat
              amount={
                (totalBookingValue.amount.amountZD +
                  Math.abs(totalCancelled?.amount.amountZD ?? 0)) /
                booking.spaces.length
              }
              currencyIso={totalBookingValue.amount.currency}
            />{' '}
            on {DateTime.fromISO(booking.created).toFormat('dd/MM/yyyy')}
          </>
        }
        amountZD={
          totalBookingValue.amount.amountZD +
          Math.abs(totalCancelled?.amount.amountZD ?? 0)
        }
        currency={totalBookingValue.amount.currency}
        isIndented
        className="small"
      />

      {totalCancelled && (
        <InvoiceRowItem
          lineText={
            <>
              {totalCancelled.numberOfSpaces > 1
                ? `${totalCancelled.numberOfSpaces} spaces`
                : '1 space'}{' '}
              cancelled on{' '}
              {DateTime.fromISO(totalCancelled.created).toFormat('dd/MM/yyyy')}
            </>
          }
          amountZD={totalCancelled.amount.amountZD}
          currency={totalCancelled.amount.currency}
          isIndented
          className="small"
        />
      )}

      {discretionaryChange && (
        <InvoiceRowItem
          lineText={
            <>
              Discretionary change on{' '}
              {DateTime.fromISO(discretionaryChange.created).toFormat(
                'dd/MM/yyyy',
              )}
            </>
          }
          amountZD={discretionaryChange.amount.amountZD}
          currency={discretionaryChange.amount.currency}
          isIndented
          className="small"
        />
      )}
    </>
  );
}
